import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ROWS = [
  ["Fund Custody", "Preload into centralized bank/app balance", "100% Self-Custody in Trust Wallet"],
  ["Top-Up Process", "Manual conversion and preloading required", "Zero Top-Ups (Real-time USDT debit)"],
  ["Joining Cashback", "None / Delayed in lockup tokens", "Instant USDT deposited on activation"],
  ["Everyday Spend Cashback", "0.5% – 2% in volatile utility tokens", "Up to 10% USDT on all online/offline spends"],
  ["Card Fees", "Monthly maintenance & annual renewal fees", "$0 Joining Fee / $0 Annual Fee (Lifetime Free)"],
];

const CSS = `
.scalio-why{position:relative;z-index:1;width:100%;padding:130px 6vw!important;background:linear-gradient(180deg,#f6f7fb,#eceef6);color:#0d0e14;font-family:Outfit,system-ui,sans-serif;line-height:1.5;-webkit-font-smoothing:antialiased}
.scalio-why,.scalio-why *{box-sizing:border-box;margin:0;padding:0}
.scalio-why .why-heading{display:grid;grid-template-columns:minmax(0,1fr) 380px;align-items:end;gap:40px;margin-bottom:60px}
.scalio-why .why-heading h2{font-family:inherit;font-size:clamp(34px,5vw,72px);font-weight:600;letter-spacing:-.035em;line-height:1.05;max-width:820px;color:#0d0e14!important;-webkit-text-fill-color:#0d0e14!important;background:none!important;text-align:left}
.scalio-why .why-heading p{max-width:380px;font-size:16px;line-height:1.5;color:#5b5e6e;text-align:left}
.scalio-why .why-word{display:inline-block;overflow:hidden;vertical-align:top;padding-bottom:.12em}
.scalio-why .why-word-inner{display:inline-block}
.scalio-why .why-table{display:grid;gap:14px}
.scalio-why .why-row{display:grid;grid-template-columns:1.1fr 1.5fr 1.5fr;gap:24px;padding:22px 28px;align-items:center;border-radius:20px;transition:transform .4s,border-color .3s}
.scalio-why .why-card{background:linear-gradient(135deg,rgba(255,255,255,.95),rgba(255,255,255,.7));border:1px solid rgba(20,24,60,.12);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);box-shadow:0 20px 50px rgba(30,36,90,.12),inset 0 1px 0 #fff}
.scalio-why .why-card:hover{transform:translateX(10px) scale(1.01);border-color:rgba(90,105,230,.6)}
.scalio-why .why-column-heading{color:#5b5e6e;font-size:14px;padding:0 28px}
.scalio-why .why-others{color:#5b5e6e}
.scalio-why .why-scalio{color:#2c3480;font-weight:500}
.scalio-why .why-column-heading .why-scalio{font-weight:600}
.scalio-why .why-label{display:none}
@media(max-width:1000px){.scalio-why .why-heading{grid-template-columns:1fr;gap:24px}}
@media(max-width:900px){.scalio-why .why-row{grid-template-columns:1fr;gap:6px}.scalio-why .why-column-heading{display:none}.scalio-why .why-label{display:inline}}
@media(max-width:600px){.scalio-why{padding:84px 20px!important}.scalio-why .why-heading{gap:16px;margin-bottom:36px}.scalio-why .why-heading h2{font-size:clamp(32px,10vw,44px)}.scalio-why .why-heading p{max-width:none;font-size:15px}.scalio-why .why-row{padding:18px 20px}.scalio-why .why-row strong{font-size:16px}.scalio-why .why-card{background:linear-gradient(135deg,rgba(255,255,255,.97),rgba(255,255,255,.86));box-shadow:0 10px 30px rgba(30,36,90,.1),inset 0 1px 0 #fff}}
@media(hover:none){.scalio-why .why-card:hover{transform:none}}
`;

function Words({ text }) {
  return text.split(" ").map((word, index) => (
    <span className="why-word" key={index}><span className="why-word-inner">{word}&nbsp;</span></span>
  ));
}

export default function WhyScalio({ id = "compare", className = "" }) {
  const root = useRef(null);
  useEffect(() => {
    const href = "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";
    if (!Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(link => link.href === href)) {
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.href = href; document.head.appendChild(link);
    }
    const context = gsap.context(() => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const heading = root.current.querySelector("h2");
      gsap.from(heading.querySelectorAll(".why-word-inner"), { yPercent: 110, stagger: .06, duration: 1, ease: "power4.out", scrollTrigger: { trigger: heading, start: "top 88%" } });
      const paragraph = root.current.querySelector(".why-heading p");
      gsap.from(paragraph, { y: 60, opacity: 0, duration: 1, ease: "power3.out", scrollTrigger: { trigger: paragraph, start: "top 90%" } });
      const table = root.current.querySelector(".why-table");
      gsap.from(table.children, { y: 70, opacity: 0, stagger: .15, duration: 1, ease: "power3.out", scrollTrigger: { trigger: table, start: "top 85%" } });
    }, root);
    ScrollTrigger.refresh();
    return () => context.revert();
  }, []);
  return (
    <section ref={root} id={id} className={`scalio-why ${className}`}>
      <style>{CSS}</style>
      <div className="why-heading">
        <h2><Words text="Why Scalio is Built" /><br /><Words text="Differently" /></h2>
        <p>Experience true non-custodial crypto payments without traditional debit card friction.</p>
      </div>
      <div className="why-table">
        <div className="why-row why-column-heading" aria-hidden="true"><span /><span>Traditional Prepaid Crypto Cards</span><span className="why-scalio">Scalio USDT Card</span></div>
        {ROWS.map(([title, others, scalio]) => (
          <article className="why-row why-card" key={title}>
            <strong>{title}</strong>
            <span className="why-others"><span className="why-label">Others: </span>{others}</span>
            <span className="why-scalio"><span className="why-label">Scalio: </span>{scalio}</span>
          </article>
        ))}
      </div>
    </section>
  );
}
